import * as React from "react";
import type { SANetProceeds } from "@/data/sellerAnalysisTypes";
import { formatPriceFull } from "@/lib/format";
import { Reveal, Overline } from "@/components/presentation/primitives";

interface Props {
  netProceeds: SANetProceeds;
}

export const SA_NetProceeds: React.FC<Props> = ({ netProceeds }) => {
  if (!netProceeds?.rows?.length) return null;
  const { costs, costSummary, rows, breakEven, intro } = netProceeds;

  return (
    <section
      className="w-full bg-ivory-alt py-20 md:py-28"
      data-testid="sa-net-proceeds"
      aria-label="What you could walk away with"
    >
      <div className="mx-auto w-full max-w-5xl px-6 md:px-10">
        <Reveal>
          <Overline>What you could walk away with</Overline>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-charcoal md:text-4xl lg:text-5xl">
            The sale price is only part of the picture.
          </h2>
          <p className="mt-6 max-w-2xl font-serif text-xl italic text-charcoal md:text-2xl">
            What ultimately matters is what you keep after the transaction.
          </p>
          {intro && (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-charcoal-soft md:text-lg">
              {intro}
            </p>
          )}
        </Reveal>

        {/* Costs */}
        {costs?.length > 0 && (
          <Reveal delay={0.05}>
            <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-5 border-y border-charcoal/10 py-8 sm:grid-cols-2">
              {costs.map((c) => (
                <div key={c.label} className="flex items-baseline justify-between gap-4">
                  <div>
                    <p className="text-base text-charcoal">{c.label}</p>
                    {c.note && (
                      <p className="mt-0.5 text-sm text-charcoal-muted">{c.note}</p>
                    )}
                  </div>
                  <span className="shrink-0 font-serif text-lg text-charcoal">
                    {c.rate ?? formatPriceFull(c.amount)}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        )}
        {costSummary && (
          <Reveal delay={0.05}>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-charcoal-muted">
              {costSummary}
            </p>
          </Reveal>
        )}

        {/* Net proceeds — table on desktop, cards on mobile */}
        <Reveal delay={0.1}>
          <p className="mt-14 text-[0.7rem] uppercase tracking-[0.24em] text-brass">
            Estimated net proceeds
          </p>

          {/* Desktop / tablet table */}
          <div className="mt-5 hidden overflow-hidden border border-charcoal/12 md:block">
            <table className="w-full border-collapse text-left" data-testid="net-table">
              <thead>
                <tr className="bg-ivory-surface text-[0.62rem] uppercase tracking-[0.16em] text-charcoal-muted">
                  <th className="px-6 py-4 font-medium">Scenario</th>
                  <th className="px-6 py-4 font-medium">Sale price</th>
                  <th className="px-6 py-4 font-medium">Est. costs</th>
                  <th className="px-6 py-4 font-medium">Net to you</th>
                  <th className="px-6 py-4 font-medium">vs. purchase</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    className={`border-t border-charcoal/10 align-top ${
                      r.highlight ? "bg-brass-soft" : "bg-ivory"
                    }`}
                  >
                    <td className="px-6 py-5 text-sm text-charcoal-soft">{r.label}</td>
                    <td className="px-6 py-5 font-serif text-lg text-charcoal">
                      {r.salePriceLabel ?? formatPriceFull(r.salePrice)}
                    </td>
                    <td className="px-6 py-5 text-sm text-charcoal-soft">{r.costs}</td>
                    <td className="px-6 py-5 font-serif text-lg text-charcoal">{r.net}</td>
                    <td className="px-6 py-5 text-sm text-charcoal-soft">{r.vsPurchase}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mt-5 space-y-4 md:hidden">
            {rows.map((r, i) => (
              <div
                key={i}
                className={`border p-5 ${
                  r.highlight
                    ? "border-brass bg-brass-soft"
                    : "border-charcoal/12 bg-ivory-surface"
                }`}
              >
                {r.label && (
                  <p className="text-[0.62rem] uppercase tracking-[0.16em] text-charcoal-muted">
                    {r.label}
                  </p>
                )}
                <p className="mt-2 font-serif text-2xl text-charcoal">
                  {r.salePriceLabel ?? formatPriceFull(r.salePrice)}
                </p>
                <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-charcoal/10 pt-4 text-sm">
                  <div>
                    <dt className="text-[0.6rem] uppercase tracking-[0.12em] text-charcoal-muted">Costs</dt>
                    <dd className="mt-0.5 text-charcoal-soft">{r.costs}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.6rem] uppercase tracking-[0.12em] text-charcoal-muted">Net</dt>
                    <dd className="mt-0.5 font-serif text-base text-charcoal">{r.net}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.6rem] uppercase tracking-[0.12em] text-charcoal-muted">vs. buy</dt>
                    <dd className="mt-0.5 text-charcoal-soft">{r.vsPurchase}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        </Reveal>

        {breakEven && (
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col gap-4 border-l-2 border-brass pl-6 md:flex-row md:items-end md:gap-12">
              <div>
                <p className="text-[0.7rem] uppercase tracking-[0.18em] text-charcoal-muted">
                  Break-even sale price
                </p>
                <p className="mt-1 font-serif text-3xl text-charcoal md:text-4xl">
                  {formatPriceFull(breakEven.salePrice)}
                </p>
              </div>
              <div className="max-w-md text-sm leading-relaxed text-charcoal-soft">
                {breakEven.gapVsMarket && (
                  <p className="text-charcoal">
                    The gap vs. market: {breakEven.gapVsMarket}.
                  </p>
                )}
                {breakEven.note && <p className="mt-1">{breakEven.note}</p>}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
